import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { ArrowRight, Brain, Check, EyeOff, Link2, MessageSquare, Trash2, Upload } from "lucide-react"

import { BotonComentario } from "@/components/Comentarios"
import { ReasonDialog } from "@/components/ReasonDialog"

import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { Toolbar, ToolbarField } from "@/components/Toolbar"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { useAuth, useRbac } from "@/lib/auth"
import { formatAmount, formatDate } from "@/lib/format"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import {
  anotarMovimiento,
  borrarReglaDeCategoria,
  categorizarMovimientos,
  conciliarMovimiento,
  fetchMovimientosBancarios,
  type FiltroDeMovimientos,
  type MovimientoBancario,
} from "@/routes/finance/api"
import { fetchBancos } from "@/routes/finance/datosApi"

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
               "agosto", "septiembre", "octubre", "noviembre", "diciembre"]
const SIN = "__sin__"
const TANDA = 120

/**
 * EL ARMADO DE LA CAJA, EN EL MAESTRO.
 *
 * Cesar, 2026-10-04: «sigo sin ver la parte de armado de flujo de caja con los
 * movimientos bancarios, debería de tener en el maestro una parte de eso», y
 * después: «para poder ver todos los movimientos y poder editar y comentar
 * cuáles sin considerar y cómo, en qué parte».
 *
 * Aquí está TODO lo que pasó por las cuentas, y por cada movimiento las tres
 * decisiones que arman la caja:
 *
 *  - EN QUÉ PARTE cae: la categoría (cobros de ventas, pagos de gastos,
 *    planilla, impuestos, transferencias…). Es la fila del flujo de caja. Al
 *    ponerla se puede RECORDAR la contraparte, y el siguiente extracto se
 *    categoriza solo (`bank_movement_regla`).
 *  - SI SE CONSIDERA: un movimiento apartado sigue aquí y en la conciliación,
 *    pero no suma en ninguna fila de la caja.
 *  - UNA NOTA: el porqué, para quien lo mire el mes que viene.
 *
 * Los movimientos se CARGAN en Carga por archivo (plantilla «Movimientos
 * bancarios»); esta pantalla enlaza allí. No se crean a mano a propósito: un
 * extracto se sube, no se teclea.
 */
export function MovimientosBancariosPage() {
  const { activeTenantId } = useAuth()
  const { can } = useRbac()
  const { toast } = useToast()
  const qc = useQueryClient()
  const puedeEditar = can("edit")

  const hoy = new Date()
  const [anio, setAnio] = React.useState(hoy.getFullYear())
  const [mes, setMes] = React.useState<number | null>(null)
  const [cuentaId, setCuentaId] = React.useState<string | null>(null)
  const [solo, setSolo] = React.useState<FiltroDeMovimientos | null>(null)
  const [elegidos, setElegidos] = React.useState<Record<string, boolean>>({})
  const [categorizando, setCategorizando] = React.useState<{ ids: string[]; contrapartes: string[]; categoria?: string } | null>(null)
  const [anotando, setAnotando] = React.useState<MovimientoBancario | null>(null)
  // A QUÉ FACTURA. Cesar: «debería de salir a qué factura (venta, gasto o no
  // reconocida) para que funcione bien el cuadre». La sugerencia se confirma
  // aquí mismo; lo que no tiene pareja se concilia a mano en Cuadres.
  const [confirmando, setConfirmando] = React.useState<MovimientoBancario | null>(null)
  // POR TANDAS: un año son cientos de filas y una tabla de doce mil píxeles no
  // se revisa, se desplaza. Se enseñan las primeras y se pide el resto.
  const [visibles, setVisibles] = React.useState(TANDA)
  React.useEffect(() => { setVisibles(TANDA) }, [anio, mes, cuentaId, solo])

  const bancos = useQuery({ queryKey: ["bancos"], queryFn: fetchBancos, enabled: Boolean(activeTenantId) })
  const q = useQuery({
    queryKey: ["movimientos-bancarios", activeTenantId, anio, mes, cuentaId, solo],
    queryFn: () => fetchMovimientosBancarios({ anio, mes, cuenta_id: cuentaId, solo }),
    enabled: Boolean(activeTenantId),
  })
  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ["movimientos-bancarios"] })
    void qc.invalidateQueries({ queryKey: ["finance-caja-bancos"] })
    void qc.invalidateQueries({ queryKey: ["finance-salud"] })
  }

  const confirmar = useMutation({
    mutationFn: (d: { id: string; tipo: "factura" | "comprobante"; refId: string; motivo: string }) =>
      conciliarMovimiento(d.id, d.tipo, d.refId, d.motivo),
    onSuccess: () => {
      toast({ title: t("Movimiento conciliado") })
      setConfirmando(null)
      refrescar()
      void qc.invalidateQueries({ queryKey: ["finance-conciliacion-bancaria"] })
    },
    onError: onWriteError("la conciliación"),
  })

  const borrarRegla = useMutation({
    mutationFn: (id: string) => borrarReglaDeCategoria(id),
    onSuccess: () => { toast({ title: t("Regla borrada"), description: t("Lo ya categorizado se queda como está.") }); refrescar() },
    onError: onWriteError("la regla", "borrar"),
  })

  const d = q.data
  const idsElegidos = Object.keys(elegidos).filter((k) => elegidos[k])
  const categorias = d?.categorias ?? []

  const abrirCategorizar = (ids: string[]) => {
    const contrapartes = Array.from(new Set(
      (d?.movimientos ?? []).filter((m) => ids.includes(m.id) && m.contraparte).map((m) => m.contraparte as string)))
    setCategorizando({ ids, contrapartes })
  }

  return (
    <div className="space-y-3">
      <MetricRow className="lg:grid-cols-4">
        <MetricCard label={t("Movimientos")} value={d?.resumen.movimientos ?? 0} loading={q.isLoading} integer />
        <MetricCard label={t("Sin categoría")} value={d?.resumen.sin_categoria ?? 0} loading={q.isLoading} integer
                    tono={(d?.resumen.sin_categoria ?? 0) > 0 ? "alerta" : "neutro"} />
        <MetricCard label={t("Sin factura reconocida")} value={d?.resumen.no_reconocidas ?? 0} loading={q.isLoading} integer />
        <MetricCard label={t("Con sugerencia de factura")} value={d?.resumen.sugeridas ?? 0} loading={q.isLoading} integer />
      </MetricRow>

      <Toolbar>
        <ToolbarField label={t("Año")} htmlFor="mov-anio">
          <Select value={String(anio)} onValueChange={(v) => setAnio(Number(v))}>
            <SelectTrigger id="mov-anio" className="w-24"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[hoy.getFullYear() + 1, hoy.getFullYear(), hoy.getFullYear() - 1, hoy.getFullYear() - 2].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToolbarField>
        <ToolbarField label={t("Mes")} htmlFor="mov-mes">
          <Select value={mes ? String(mes) : SIN} onValueChange={(v) => setMes(v === SIN ? null : Number(v))}>
            <SelectTrigger id="mov-mes" className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={SIN}>{t("Todo el año")}</SelectItem>
              {MESES.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{t(m)}</SelectItem>)}
            </SelectContent>
          </Select>
        </ToolbarField>
        <ToolbarField label={t("Cuenta")} htmlFor="mov-cuenta">
          <Select value={cuentaId ?? SIN} onValueChange={(v) => setCuentaId(v === SIN ? null : v)}>
            <SelectTrigger id="mov-cuenta" className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={SIN}>{t("Todas las cuentas")}</SelectItem>
              {(bancos.data ?? []).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </ToolbarField>
        <ToolbarField label={t("Ver")} htmlFor="mov-solo">
          <Select value={solo ?? SIN} onValueChange={(v) => setSolo(v === SIN ? null : (v as FiltroDeMovimientos))}>
            <SelectTrigger id="mov-solo" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={SIN}>{t("Todos")}</SelectItem>
              <SelectItem value="sin_categoria">{t("Sin categoría")}</SelectItem>
              <SelectItem value="no_reconocidas">{t("Sin factura reconocida")}</SelectItem>
              <SelectItem value="no_considerados">{t("Apartados de la caja")}</SelectItem>
              <SelectItem value="con_nota">{t("Con nota")}</SelectItem>
            </SelectContent>
          </Select>
        </ToolbarField>
        <div className="ml-auto flex items-center gap-1.5">
          {puedeEditar && idsElegidos.length > 0 ? (
            <Button size="sm" onClick={() => abrirCategorizar(idsElegidos)}>
              {t("Categorizar")} {idsElegidos.length}
            </Button>
          ) : null}
          <Button size="sm" variant="outline" asChild>
            <Link to="/contabilidad/maestros/carga?plantilla=movimientos-bancarios">
              <Upload />
              {t("Cargar extracto")}
            </Link>
          </Button>
        </div>
      </Toolbar>

      <WorkPanel
        title={t("Movimientos bancarios")}
        meta={<span className="text-2xs text-muted-foreground">{t("cada uno, en qué fila de la caja cae")}</span>}
        footnote={t("La categoría es la fila del flujo de caja. Un movimiento apartado sigue aquí y en la conciliación, pero no suma en la caja. Lo que categoriza una persona no lo pisa ni el archivo ni una regla.")}
      >
        <DataState
          loading={q.isLoading}
          error={q.error}
          empty={Boolean(d) && (d?.movimientos.length ?? 0) === 0}
          emptyMessage={solo ? t("Nada con ese filtro.") : t("No hay movimientos bancarios cargados para este período.")}
          emptyHint={t("Se cargan desde Carga por archivo, con la plantilla «Movimientos bancarios (extracto)»: un archivo por cuenta, con fecha, importe con signo y, si lo tienes, contraparte y categoría.")}
          onRetry={() => void q.refetch()}
          rows={6}
        >
          <Table>
            <TableHeader>
              <TableRow>
                {puedeEditar ? <TableHead className="w-8" /> : null}
                <TableHead className="w-24">{t("Fecha")}</TableHead>
                <TableHead className="w-40">{t("Cuenta")}</TableHead>
                <TableHead>{t("Movimiento")}</TableHead>
                <TableHead className="w-32 text-right">{t("Importe")}</TableHead>
                <TableHead className="w-28 text-right">USD</TableHead>
                <TableHead className="w-52">{t("En la caja va a")}</TableHead>
                <TableHead className="min-w-[14rem]">{t("Factura")}</TableHead>
                <TableHead className="w-28">{t("Nota")}</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(d?.movimientos ?? []).slice(0, visibles).map((m) => (
                <TableRow key={m.id} className={cn(!m.considerar && "opacity-60", !m.categoria && m.considerar && "bg-warning-soft/30")}>
                  {puedeEditar ? (
                    <TableCell>
                      <Checkbox checked={Boolean(elegidos[m.id])}
                                onCheckedChange={(v) => setElegidos((p) => ({ ...p, [m.id]: Boolean(v) }))}
                                aria-label={t("Elegir")} />
                    </TableCell>
                  ) : null}
                  <TableCell className="code text-2xs text-muted-foreground">{formatDate(m.fecha)}</TableCell>
                  <TableCell className="truncate text-2xs" title={m.cuenta}>{m.cuenta}</TableCell>
                  <TableCell className="text-xs">
                    <span className="flex items-center gap-1">
                      <span className="block min-w-0 flex-1 truncate" title={m.descripcion ?? ""}>{m.descripcion || "—"}</span>
                      <BotonComentario ancla={{ ambito: "general", referencia: `movimiento:${m.id}`,
                                                rotulo: `${t("Movimiento")} ${formatDate(m.fecha)} · ${formatAmount(m.importe)} ${m.moneda} · ${m.cuenta}` }} />
                    </span>
                    <span className="text-2xs text-muted-foreground">
                      {m.contraparte ? `${m.contraparte} · ` : ""}{m.referencia ? `ref. ${m.referencia}` : ""}
                      {m.conciliado ? ` · ${t("conciliado")}` : ""}
                    </span>
                  </TableCell>
                  <TableCell className={cn("num", m.importe < 0 && "text-destructive", !m.considerar && "line-through")}>
                    {formatAmount(m.importe)} <span className="text-2xs text-muted-foreground">{m.moneda}</span>
                  </TableCell>
                  <TableCell className="num text-muted-foreground">{m.importe_usd == null ? "—" : formatAmount(m.importe_usd)}</TableCell>
                  <TableCell>
                    {puedeEditar ? (
                      <div className="flex items-center gap-1.5">
                        <Select value={m.categoria ?? SIN}
                                onValueChange={(v) => { if (v !== SIN) setCategorizando({ ids: [m.id], contrapartes: m.contraparte ? [m.contraparte] : [], categoria: v }) }}>
                          <SelectTrigger className={cn("h-7 w-44 text-2xs", !m.categoria && "border-warning/60")}>
                            <SelectValue placeholder={t("Sin categoría")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SIN} disabled>{t("Sin categoría")}</SelectItem>
                            {categorias.map((c) => <SelectItem key={c.id} value={c.id}>{t(c.nombre)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {m.categoria_por === "regla" ? <Brain className="h-3 w-3 text-muted-foreground" aria-label={t("por regla")} /> : null}
                      </div>
                    ) : (
                      <span className="text-xs">{m.nombre_categoria ? t(m.nombre_categoria) : <Badge variant="warning-soft" estado>{t("sin categoría")}</Badge>}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-2xs">
                    <Factura m={m} puedeEditar={puedeEditar} onConfirmar={() => setConfirmando(m)} />
                  </TableCell>
                  <TableCell className="text-2xs text-muted-foreground">
                    {!m.considerar ? <Badge variant="outline" estado><EyeOff />{t("apartado")}</Badge> : null}
                    {m.nota ? <span className="block truncate" title={m.nota}>{m.nota}</span> : null}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {puedeEditar ? (
                      <Button size="xs" variant="ghost" onClick={() => setAnotando(m)} title={t("Considerar o no, y una nota")}>
                        <MessageSquare />
                        {t("Anotar")}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(d?.movimientos.length ?? 0) > visibles ? (
            <div className="flex items-center justify-between border-t px-2.5 py-1.5 text-2xs text-muted-foreground">
              <span>{visibles} {t("de")} {d?.movimientos.length}</span>
              <Button size="xs" variant="outline" onClick={() => setVisibles((v) => v + TANDA)}>
                {t("Ver más")}
              </Button>
            </div>
          ) : null}
        </DataState>
      </WorkPanel>

      {(d?.reglas.length ?? 0) > 0 ? (
        <WorkPanel
          title={t("Lo que Marty aprendió")}
          meta={<span className="text-2xs text-muted-foreground">{d?.reglas.length} {t("contraparte(s) con categoría fija")}</span>}
          footnote={t("Al cargar un extracto, los movimientos de estas contrapartes se categorizan solos. Borrar una regla no toca lo ya categorizado.")}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Contraparte")}</TableHead>
                <TableHead className="w-48">{t("Va a")}</TableHead>
                <TableHead className="w-28 text-right">{t("Aplicada")}</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(d?.reglas ?? []).map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{r.patron}</TableCell>
                  <TableCell className="text-xs">{t(r.nombre_categoria)}</TableCell>
                  <TableCell className="num text-muted-foreground">{r.veces_aplicada} {t("veces")}</TableCell>
                  <TableCell className="text-right">
                    {puedeEditar ? (
                      <Button size="xs" variant="ghost" onClick={() => borrarRegla.mutate(r.id)} disabled={borrarRegla.isPending} aria-label={t("Borrar regla")}>
                        <Trash2 />
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </WorkPanel>
      ) : null}

      <p className="text-2xs text-muted-foreground">
        <Link to="/finanzas/caja" className="inline-flex items-center gap-1 hover:underline">
          {t("Ver cómo queda la caja")} <ArrowRight className="h-3 w-3" />
        </Link>
      </p>

      {categorizando ? (
        <CategorizarDialog
          ids={categorizando.ids}
          contrapartes={categorizando.contrapartes}
          categoriaInicial={categorizando.categoria ?? null}
          categorias={categorias}
          onClose={() => { setCategorizando(null); setElegidos({}) }}
          onHecho={refrescar}
        />
      ) : null}
      {anotando ? <AnotarDialog mov={anotando} onClose={() => setAnotando(null)} onHecho={refrescar} /> : null}
      {confirmando?.factura.sugerencia ? (
        <ReasonDialog
          open
          onOpenChange={(v) => { if (!v) setConfirmando(null) }}
          title={`${t("Conciliar con")} ${confirmando.factura.sugerencia.etiqueta}`}
          description={`${formatAmount(confirmando.importe)} ${confirmando.moneda} · ${confirmando.factura.sugerencia.por_que}.`}
          confirmLabel={t("Conciliar")}
          pending={confirmar.isPending}
          suggestions={["Confirmado contra el extracto."]}
          onConfirm={(motivo) => confirmar.mutateAsync({
            id: confirmando.id, tipo: confirmando.factura.sugerencia!.tipo,
            refId: confirmando.factura.sugerencia!.id, motivo,
          }).then(() => undefined)}
        />
      ) : null}
    </div>
  )
}

/**
 * A QUÉ FACTURA RESPONDE. Venta (factura emitida) o gasto (comprobante) cuando
 * está conciliado; la sugerencia con «Es ésta» cuando la hay; «no reconocida»
 * cuando nada la explica, con el camino a Cuadres para conciliarla a mano.
 */
function Factura({ m, puedeEditar, onConfirmar }: { m: MovimientoBancario; puedeEditar: boolean; onConfirmar: () => void }) {
  const f = m.factura
  if (f.estado === "venta" || f.estado === "gasto") {
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <Badge variant="success-soft" estado>{f.estado === "venta" ? t("venta") : t("gasto")}</Badge>
        <span className="truncate" title={f.etiqueta ?? ""}>{f.etiqueta}</span>
        {f.automatica ? <span className="text-muted-foreground">· Marty</span> : null}
      </span>
    )
  }
  if (f.estado === "a_mano") {
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <Badge variant="outline" estado>{t("a mano")}</Badge>
        <span className="truncate text-muted-foreground" title={f.etiqueta ?? ""}>{f.etiqueta}</span>
      </span>
    )
  }
  if (f.estado === "sugerida" && f.sugerencia) {
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <Badge variant="brand-soft" estado>{t("sugerida")}</Badge>
        <span className="truncate" title={f.sugerencia.por_que}>{f.sugerencia.etiqueta}</span>
        {puedeEditar ? (
          <Button size="xs" variant={f.sugerencia.confianza === "exacta" ? "default" : "outline"} onClick={onConfirmar}>
            <Check />
            {t("Es ésta")}
          </Button>
        ) : null}
      </span>
    )
  }
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Badge variant="warning-soft" estado>{t("no reconocida")}</Badge>
      {puedeEditar ? (
        <Link to="/finanzas/cuadres" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground hover:underline">
          <Link2 className="h-3 w-3" />
          {t("Conciliar en Cuadres")}
        </Link>
      ) : null}
    </span>
  )
}

/**
 * EN QUÉ PARTE, Y SI SE RECUERDA. El diálogo existe por la segunda pregunta:
 * recordar la contraparte es lo que hace que el mes que viene no haya que
 * volver a categorizar. Se ofrece sólo cuando hay contraparte que recordar.
 */
function CategorizarDialog({
  ids, contrapartes, categoriaInicial, categorias, onClose, onHecho,
}: {
  ids: string[]; contrapartes: string[]; categoriaInicial: string | null
  categorias: Array<{ id: string; nombre: string }>
  onClose: () => void; onHecho: () => void
}) {
  const { toast } = useToast()
  const [categoria, setCategoria] = React.useState<string>(categoriaInicial ?? "")
  const [recordar, setRecordar] = React.useState(contrapartes.length > 0)
  const guardar = useMutation({
    mutationFn: () => categorizarMovimientos({ ids, categoria, recordar: recordar && contrapartes.length > 0 }),
    onSuccess: (r) => {
      toast({
        title: `${r.categorizados} ${t("movimiento(s) categorizados")}`,
        description: r.reglas
          ? `${t("Recordé")} ${r.reglas} ${t("contraparte(s)")}${r.por_regla ? ` · ${r.por_regla} ${t("más categorizados por la regla")}` : ""}.`
          : undefined,
      })
      onHecho(); onClose()
    },
    onError: onWriteError("la categoría"),
  })
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("En qué parte de la caja")}</DialogTitle>
          <DialogDescription>
            {ids.length === 1 ? t("Este movimiento") : `${ids.length} ${t("movimientos")}`}
            {contrapartes.length ? ` · ${contrapartes.slice(0, 3).join(", ")}${contrapartes.length > 3 ? "…" : ""}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="cat-cat">{t("Categoría")}</Label>
            <Select value={categoria} onValueChange={setCategoria}>
              <SelectTrigger id="cat-cat"><SelectValue placeholder={t("Elige")} /></SelectTrigger>
              <SelectContent>
                {categorias.map((c) => <SelectItem key={c.id} value={c.id}>{t(c.nombre)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {contrapartes.length ? (
            <label className="flex items-start gap-2 text-xs">
              <Checkbox checked={recordar} onCheckedChange={(v) => setRecordar(Boolean(v))} className="mt-0.5" />
              <span>
                {t("Recordarlo para esta contraparte")}
                <span className="block text-2xs text-muted-foreground">
                  {t("El próximo extracto la categoriza solo, y los movimientos que ya tiene y nadie miró también.")}
                </span>
              </span>
            </label>
          ) : (
            <p className="text-2xs text-muted-foreground">{t("Sin contraparte no hay nada que recordar: el archivo no la trae.")}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
          <Button onClick={() => guardar.mutate()} disabled={!categoria || guardar.isPending}>{t("Guardar")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Considerar o apartar, y la nota. Las dos cosas en un sitio: apartar sin decir por qué es lo que no se quiere. */
function AnotarDialog({ mov, onClose, onHecho }: { mov: MovimientoBancario; onClose: () => void; onHecho: () => void }) {
  const { toast } = useToast()
  const [considerar, setConsiderar] = React.useState(mov.considerar)
  const [nota, setNota] = React.useState(mov.nota ?? "")
  const guardar = useMutation({
    mutationFn: () => anotarMovimiento(mov.id, { considerar, nota }),
    onSuccess: (r) => {
      toast({ title: r.considerar ? t("Se considera en la caja") : t("Apartado de la caja") })
      onHecho(); onClose()
    },
    onError: onWriteError("la nota"),
  })
  const puede = considerar || nota.trim().length >= 3
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{formatAmount(mov.importe)} {mov.moneda} · {formatDate(mov.fecha)}</DialogTitle>
          <DialogDescription>{mov.descripcion || mov.contraparte || mov.cuenta}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-start gap-2 text-xs">
            <Checkbox checked={considerar} onCheckedChange={(v) => setConsiderar(Boolean(v))} className="mt-0.5" />
            <span>
              {t("Se considera en la caja")}
              <span className="block text-2xs text-muted-foreground">
                {t("Desmárcalo para apartarlo: sigue en el extracto y en la conciliación, pero no suma en ninguna fila.")}
              </span>
            </span>
          </label>
          <div className="space-y-1">
            <Label htmlFor="nota-mov">{t("Nota")}</Label>
            <Textarea id="nota-mov" rows={2} value={nota} onChange={(e) => setNota(e.target.value)}
                      placeholder={t("Devolución ya neteada en el cobro del 12/09")} />
            {!considerar ? <p className="text-2xs text-muted-foreground">{t("Para apartarlo hace falta decir por qué.")}</p> : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
          <Button onClick={() => guardar.mutate()} disabled={!puede || guardar.isPending}>{t("Guardar")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
